/**
 * OneVeracity Design Tokens — Colors
 *
 * Single source of truth for all color values in the application.
 * Aligned to UX_GUIDE_UNIFIED.md and OneVeracity Brand Guidelines v2.0.
 *
 * IMPORTANT: Veracity Orange (#D6461D) must always be used at 100% opacity.
 * Secondary colors may use opacity in 10% increments.
 */

// =============================================================================
// Brand Palette
// =============================================================================

export const brand = {
  veracityOrange: '#D6461D',
  daybreakAmber: '#F79E31',
  morningCoral: '#EE6352',
  horizonBlue: '#A4C0C1',
  twilightPlum: '#553746',
  duskBlue: '#3F4C5C',
  clarityGreen: '#597B4B',
  midnightCharcoal: '#1C1919',
  gentleHaze: '#FFE6C8',
  firstLight: '#FFF1E0',
} as const;

// =============================================================================
// Light Theme Palette
// =============================================================================

export const light = {
  bgBase: '#FFF1E0',       // First Light — page background
  bgSurface: '#ffffff',    // Cards, panels
  bgElevated: '#FFE6C8',   // Gentle Haze — hover, selected rows
  border: '#e8d5bc',
  textPrimary: '#1C1919',  // Midnight Charcoal
  textSecondary: '#6b5240',
  textMuted: '#a8896b',
  accent: '#D6461D',
  accentSubtle: '#fce8e2',
} as const;

// =============================================================================
// Dark Theme Palette
// =============================================================================

export const dark = {
  bgBase: '#1C1919',       // Midnight Charcoal
  bgSurface: '#2a2523',    // Warm-tinted dark
  bgElevated: '#363029',   // Hover, tooltips, selected
  border: '#42392f',
  textPrimary: '#FFF1E0',  // First Light
  textSecondary: '#c4a98a',
  textMuted: '#7a6450',
  accent: '#D6461D',
  accentSubtle: '#3d1c0f',
} as const;

// =============================================================================
// Semantic Status Colors
// =============================================================================

export const status = {
  positive: '#597B4B',     // Clarity Green — favorable
  negative: '#EE6352',     // Morning Coral — unfavorable/error
  warning: '#F79E31',      // Daybreak Amber — caution
  neutralDelta: '#7a6450', // Muted warm — no-judgment
  info: '#3F4C5C',         // Dusk Blue
} as const;

// =============================================================================
// Chart Series (fixed order — never skip or reorder)
// =============================================================================

export const chartSeries = [
  '#D6461D', // Veracity Orange (primary)
  '#F79E31', // Daybreak Amber
  '#A4C0C1', // Horizon Blue
  '#553746', // Twilight Plum
  '#3F4C5C', // Dusk Blue at 60% — use with opacity
] as const;

// =============================================================================
// Unified COLORS object (backward-compatible import for page components)
// =============================================================================

export const COLORS = {
  // Brand
  primary: brand.veracityOrange,
  primaryDark: '#B03818',

  // Semantic
  success: status.positive,
  error: status.negative,
  warning: status.warning,

  // Light theme surfaces
  white: '#FFFFFF',
  bgBase: light.bgBase,
  bgSurface: light.bgSurface,
  bgElevated: light.bgElevated,

  // Light theme text (warm neutrals replacing cool grays)
  textPrimary: light.textPrimary,
  textSecondary: light.textSecondary,
  textMuted: light.textMuted,
  disabled: '#c4a98a',
  disabledText: '#a8896b',

  // Light theme borders
  border: light.border,
  borderSubtle: '#f0dcc4',

  // Secondary brand
  secondary: brand.daybreakAmber,
  teal: brand.horizonBlue,
  plum: brand.twilightPlum,
  duskBlue: brand.duskBlue,
  green: brand.clarityGreen,

  // Surfaces
  surface: light.bgSurface,
  surfaceHover: light.bgElevated,

  // Light wash of Veracity Orange — accent-adjacent chrome (headers/bands on
  // accent-bordered blocks, active toggles). Same value as dragOver.
  accentSubtle: light.accentSubtle,

  // Drag state
  dragOver: light.accentSubtle,
} as const;

// =============================================================================
// Foreground pairing (brand guide 4.5)
// =============================================================================

/**
 * True when `hex` is one of the LIGHT palette colors and therefore needs the
 * Midnight Charcoal foreground rather than a white / First Light one.
 *
 * Brand guide 4.5 approves white foregrounds only on the dark palette (Orange,
 * Charcoal, Plum, Dusk Blue, Clarity Green); Daybreak Amber, Morning Coral and
 * Horizon Blue must pair with charcoal. This is a YIQ brightness test rather
 * than a hardcoded list of the three, so an admin-chosen color pairs correctly
 * without anyone maintaining a lookup.
 *
 * Lives here, next to the palette, because three surfaces need it: the AppShell
 * header, the launcher tile glyphs, and the admin editor's preview.
 */
export function isLightColor(hex: string): boolean {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return false;
  const n = parseInt(m[1]!, 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  return (r * 299 + g * 587 + b * 114) / 1000 >= 128;
}
